import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api.ts";

// Admin-only. Lists D1 backups in R2 and offers a "Run now" button
// for the restore-drill workflow + first-day-after-deploy catch-up.
// The daily cron (0 3 * * * UTC) handles the steady state.

export function StaffBackupsPage() {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ["staff", "backups"],
    queryFn:  api.staffListBackups,
  });
  const [runError, setRunError] = useState<string | null>(null);
  const [lastRun, setLastRun]   = useState<{ key: string; monthlyKey: string | null; rows: number; bytes: number } | null>(null);
  const run = useMutation({
    mutationFn: api.staffRunBackup,
    onSuccess: (data) => {
      setRunError(null);
      setLastRun({ key: data.key, monthlyKey: data.monthlyKey, rows: data.rows, bytes: data.bytes });
      qc.invalidateQueries({ queryKey: ["staff", "backups"] });
    },
    onError: (e: Error) => setRunError(e.message),
  });

  const fmtSize = (n: number) => {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(2)} MB`;
  };
  const fmtTime = (iso: string) => {
    const d = new Date(iso);
    return d.toISOString().replace("T", " ").slice(0, 19) + " UTC";
  };

  return (
    <div>
      <h2>D1 backups</h2>
      <p className="muted" style={{ marginTop: "-0.5rem" }}>
        Daily cron at <code>03:00 UTC</code> writes
        <code> backups/d1/YYYY-MM-DD.json.gz</code> to R2
        (<code>btquick-blobs-prod</code>). Format documented at
        <code> apps/api/src/backup.ts:BackupManifest</code>.
        Use "Run now" for restore-drill or first-day catch-up.
      </p>

      <div style={{
        display: "flex", gap: "0.75rem", alignItems: "center",
        margin: "1rem 0", padding: "0.75rem",
        background: "rgba(148,163,184,0.08)", borderRadius: "6px",
      }}>
        <button
          onClick={() => run.mutate()}
          disabled={run.isPending}
        >
          {run.isPending ? "Running…" : "Run backup now"}
        </button>
        {lastRun && (
          <span className="muted" style={{ fontSize: "0.9em" }}>
            ✓ wrote <code>{lastRun.key}</code>
            {lastRun.monthlyKey && <> + monthly <code>{lastRun.monthlyKey}</code></>}
            {" "}({lastRun.rows} rows, {fmtSize(lastRun.bytes)})
          </span>
        )}
        {runError && <span className="err">{runError}</span>}
      </div>

      {list.isError && (
        <p className="err">Couldn't load — {(list.error as Error).message}</p>
      )}

      <table className="rows">
        <thead>
          <tr>
            <th>Key</th>
            <th>Size</th>
            <th>Exported at (UTC)</th>
            <th>Duration</th>
          </tr>
        </thead>
        <tbody>
          {list.data?.backups.length === 0 && !list.isFetching && (
            <tr>
              <td colSpan={4} className="muted" style={{ padding: "1rem" }}>
                No backups yet. The first cron fires at 03:00 UTC after each
                deploy; or hit "Run backup now" above.
              </td>
            </tr>
          )}
          {list.data?.backups.map((b) => (
            <tr key={b.key}>
              <td><code>{b.key}</code></td>
              <td style={{ whiteSpace: "nowrap" }}>{fmtSize(b.size)}</td>
              <td style={{ whiteSpace: "nowrap", fontFamily: "monospace", fontSize: "0.85em" }}>
                {b.exportedAt ? fmtTime(b.exportedAt) : fmtTime(b.uploaded)}
              </td>
              <td style={{ whiteSpace: "nowrap" }}>
                {b.durationMs != null ? `${b.durationMs} ms` : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {list.data?.truncated && (
        <p className="muted" style={{ fontSize: "0.85em", marginTop: "0.5rem" }}>
          More than 1000 backup objects — listing truncated.
        </p>
      )}
    </div>
  );
}
